import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { CheckboxList } from '@/components/core';
import { Checkbox } from '@/components/ui-kit/checkbox';
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui-kit/form';
import { Input } from '@/components/ui-kit/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui-kit/select';
import { Textarea } from '@/components/ui-kit/textarea';
import { useDepartmentOptions, useDesignationOptions } from '@/modules/shared/hr-lookups';
import {
  ASSESSMENT_TYPES,
  PEER_CATEGORIES,
  REVIEW_TYPES,
  getReviewTypesForAssessment,
} from '@/modules/shared/hr-form-engine';
import { FormBasicInfoValues } from '../../schemas/form-basic-info-schema';

export interface FormBasicInfoFieldsProps {
  /** Assessment type, review type and peer category are locked once the form exists. */
  isEditMode: boolean;
}

export const FormBasicInfoFields = ({ isEditMode }: FormBasicInfoFieldsProps) => {
  const { t } = useTranslation();
  const { control, watch, setValue } = useFormContext<FormBasicInfoValues>();

  const { data: departments = [], isLoading: isDepartmentsLoading } = useDepartmentOptions();
  const { data: designations = [], isLoading: isDesignationsLoading } = useDesignationOptions();

  const assessmentType = watch('AssessmentType');
  const reviewType = watch('ReviewType');
  const applicableReviewTypes = REVIEW_TYPES.filter((type) =>
    getReviewTypesForAssessment(assessmentType).includes(type.key)
  );

  return (
    <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
      <FormField
        control={control}
        name="Title"
        render={({ field }) => (
          <FormItem className="sm:col-span-2">
            <FormLabel>{t('FORM_TITLE')}</FormLabel>
            <FormControl>
              <Input placeholder={t('FORM_TITLE')} {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={control}
        name="Description"
        render={({ field }) => (
          <FormItem className="sm:col-span-2">
            <FormLabel>{t('DESCRIPTION')}</FormLabel>
            <FormControl>
              <Textarea rows={2} placeholder={t('DESCRIPTION')} {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={control}
        name="AssessmentType"
        render={({ field }) => (
          <FormItem>
            <FormLabel>{t('ASSESSMENT_TYPE')}</FormLabel>
            <Select
              disabled={isEditMode}
              value={field.value}
              onValueChange={(value) => {
                field.onChange(value);
                setValue('ReviewType', '' as FormBasicInfoValues['ReviewType']);
                setValue('Category', undefined);
                if (value !== 'crf') setValue('ConfirmationForIntern', false);
              }}
            >
              <FormControl>
                <SelectTrigger>
                  <SelectValue placeholder={t('ASSESSMENT_TYPE')} />
                </SelectTrigger>
              </FormControl>
              <SelectContent>
                {ASSESSMENT_TYPES.map((option) => (
                  <SelectItem key={option.key} value={option.key}>
                    {t(option.title)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={control}
        name="ReviewType"
        render={({ field }) => (
          <FormItem>
            <FormLabel>{t('REVIEW_TYPE')}</FormLabel>
            <Select
              disabled={isEditMode || !assessmentType}
              value={field.value}
              onValueChange={(value) => {
                field.onChange(value);
                if (value !== 'peer') setValue('Category', undefined);
              }}
            >
              <FormControl>
                <SelectTrigger>
                  <SelectValue placeholder={t('REVIEW_TYPE')} />
                </SelectTrigger>
              </FormControl>
              <SelectContent>
                {applicableReviewTypes.map((option) => (
                  <SelectItem key={option.key} value={option.key}>
                    {t(option.title)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <FormMessage />
          </FormItem>
        )}
      />

      {reviewType === 'peer' && (
        <FormField
          control={control}
          name="Category"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t('CATEGORY')}</FormLabel>
              <Select
                disabled={isEditMode}
                value={field.value ?? ''}
                onValueChange={field.onChange}
              >
                <FormControl>
                  <SelectTrigger>
                    <SelectValue placeholder={t('CATEGORY')} />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {PEER_CATEGORIES.map((category) => (
                    <SelectItem key={category.key} value={category.key}>
                      {t(category.title)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />
      )}

      <FormField
        control={control}
        name="IsAllDepartments"
        render={({ field }) => (
          <FormItem className="flex flex-row items-center gap-2 space-y-0 sm:col-span-2">
            <FormControl>
              <Checkbox
                checked={field.value}
                onCheckedChange={(checked) => {
                  field.onChange(Boolean(checked));
                  if (checked) setValue('DepartmentIds', []);
                }}
              />
            </FormControl>
            <FormLabel className="!mt-0">{t('ALL_DEPARTMENTS')}</FormLabel>
          </FormItem>
        )}
      />

      {!watch('IsAllDepartments') && (
        <FormField
          control={control}
          name="DepartmentIds"
          render={({ field }) => (
            <FormItem className="sm:col-span-2">
              <FormLabel>{t('DEPARTMENT')}</FormLabel>
              <FormControl>
                <CheckboxList
                  options={departments.map((department) => ({
                    value: department.ItemId,
                    label: department.Title,
                  }))}
                  value={field.value ?? []}
                  onChange={field.onChange}
                  isLoading={isDepartmentsLoading}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      )}

      <FormField
        control={control}
        name="IsAllDesignations"
        render={({ field }) => (
          <FormItem className="flex flex-row items-center gap-2 space-y-0 sm:col-span-2">
            <FormControl>
              <Checkbox
                checked={field.value}
                onCheckedChange={(checked) => {
                  field.onChange(Boolean(checked));
                  if (checked) setValue('DesignationIds', []);
                }}
              />
            </FormControl>
            <FormLabel className="!mt-0">{t('ALL_DESIGNATIONS')}</FormLabel>
          </FormItem>
        )}
      />

      {!watch('IsAllDesignations') && (
        <FormField
          control={control}
          name="DesignationIds"
          render={({ field }) => (
            <FormItem className="sm:col-span-2">
              <FormLabel>{t('DESIGNATION')}</FormLabel>
              <FormControl>
                <CheckboxList
                  options={designations.map((designation) => ({
                    value: designation.ItemId,
                    label: designation.Title,
                  }))}
                  value={field.value ?? []}
                  onChange={field.onChange}
                  isLoading={isDesignationsLoading}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      )}

      {assessmentType === 'crf' && (
        <FormField
          control={control}
          name="ConfirmationForIntern"
          render={({ field }) => (
            <FormItem className="flex flex-row items-center gap-2 space-y-0">
              <FormControl>
                <Checkbox
                  checked={field.value}
                  onCheckedChange={(checked) => field.onChange(Boolean(checked))}
                />
              </FormControl>
              <FormLabel className="!mt-0">{t('CONFIRMATION_FOR_INTERN')}</FormLabel>
            </FormItem>
          )}
        />
      )}
    </div>
  );
};
